import {ControlledInput, ControlledSelect, ControlledDatepicker, clx} from '@hm/fundamental'
import moment from 'moment'
import {useFormContext, Controller, UseFormReturn} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {ShutdownType} from '@refra/modules/common/enums'
import type {ShutdownForm} from '@refra/modules/common/types'

interface KilnShutdownInputsProps {
  isReadOnly: boolean
  isRowLayout: boolean
}

export const KilnShutdownInputs: React.FC<KilnShutdownInputsProps> = ({
  isReadOnly,
  isRowLayout
}) => {
  const {t} = useTranslation()

  const {
    control,
    formState: {errors}
  }: UseFormReturn<ShutdownForm> = useFormContext()

  return (
    <form className={clx('flex gap-3', isRowLayout ? 'flex-row' : 'flex-col')}>
      <Controller
        name="shutdownType"
        control={control}
        rules={{required: t('error.required')}}
        disabled={isReadOnly}
        render={({field}) => (
          <ControlledSelect
            name={field.name}
            value={field.value}
            className={clx(!isRowLayout && 'w-96')}
            label={t('refraPlanning.kilnShutdown.intervention')}
            values={Object.values(ShutdownType).map((type) => ({
              value: type,
              label: t(`refraPlanning.kilnShutdown.${type}`)
            }))}
            isReadOnly={isReadOnly}
            error={errors.shutdownType?.message}
            onChange={(value) => field.onChange(value)}
            onBlur={field.onBlur}
            data-cy="kiln_shutdown_type_input"
          />
        )}
      />
      <Controller
        name="name"
        control={control}
        rules={{required: t('error.required')}}
        disabled={isReadOnly}
        render={({field}) => (
          <ControlledInput
            {...field}
            label={t('refraPlanning.kilnShutdown.shutdownName')}
            isReadOnly={isReadOnly}
            showValidationErrorOnTouched={false}
            error={errors.name?.message}
            className="w-96"
            data-cy="kiln_shutdown_name_input"
          />
        )}
      />
      <Controller
        name="interventionDate"
        control={control}
        rules={{
          required: t('error.required'),
          validate: (value, otherFields) => {
            if (!value || !otherFields.feedingDate) return true

            const interventionDate = moment(value)
            const feedingDate = moment(otherFields.feedingDate)

            return interventionDate.isBefore(feedingDate) ? true : t('error.interventionDate')
          }
        }}
        disabled={isReadOnly}
        render={({field}) => (
          <ControlledDatepicker
            {...field}
            label={t('refraPlanning.kilnShutdown.interventionDate')}
            value={field.value ? new Date(field.value) : null}
            isReadOnly={isReadOnly}
            showValidationErrorOnTouched={false}
            error={errors.interventionDate?.message}
            data-cy="kiln_shutdown_interventionDate_input"
            className={clx(!isRowLayout && 'w-96')}
          />
        )}
      />
      <Controller
        name="feedingDate"
        control={control}
        rules={{
          validate: (value, otherFields) => {
            if (!value || !otherFields.interventionDate) return true

            const feedingDate = moment(value)
            const interventionDate = moment(otherFields.interventionDate)

            return feedingDate.isAfter(interventionDate) ? true : t('error.feedingDate')
          }
        }}
        disabled={isReadOnly}
        render={({field}) => (
          <ControlledDatepicker
            {...field}
            label={t('refraPlanning.kilnShutdown.feedingDate')}
            value={field.value ? new Date(field.value) : null}
            isReadOnly={isReadOnly}
            showValidationErrorOnTouched={false}
            error={errors.feedingDate?.message}
            data-cy="kiln_shutdown_feedingDate_input"
            className={clx(!isRowLayout && 'w-96')}
          />
        )}
      />
    </form>
  )
}
